import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc, setDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { User as AppUser } from '../types';

interface AuthContextType {
  user: User | null;
  appUser: AppUser | null;
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        // Fetch additional user data from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data() as Omit<AppUser, 'id'>;
            setAppUser({ id: firebaseUser.uid, ...userData });
          } else {
            // User document doesn't exist, try to find by email or create new user
            console.log('User document not found, checking for existing salon setup...');

            // Check if this is emmadaosaloon@gmail.com and link to existing salon
            if (firebaseUser.email === 'emmadaosaloon@gmail.com') {
              console.log('Setting up EMMA DAO user...');
              // Find the salon for this email
              const salonsQuery = query(collection(db, 'salons'), where('email', '==', 'emmadaosaloon@gmail.com'));
              const salonsSnapshot = await getDocs(salonsQuery);

              if (!salonsSnapshot.empty) {
                const salonDoc = salonsSnapshot.docs[0];
                const salonId = salonDoc.id;

                // Create user document
                const newUserData = {
                  email: firebaseUser.email,
                  salonId: salonId,
                  role: 'owner',
                  status: 'active',
                  createdAt: Timestamp.now(),
                  updatedAt: Timestamp.now(),
                };

                await setDoc(doc(db, 'users', firebaseUser.uid), newUserData);
                setAppUser({
                  id: firebaseUser.uid,
                  email: newUserData.email,
                  displayName: firebaseUser.displayName || firebaseUser.email || 'User',
                  salonId: newUserData.salonId,
                  role: newUserData.role as 'owner' | 'staff',
                  createdAt: newUserData.createdAt.toDate(),
                  updatedAt: newUserData.updatedAt.toDate(),
                });
                console.log('EMMA DAO user setup complete');
              } else {
                console.error('No salon found for emmadaosaloon@gmail.com');
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setUser(null);
        setAppUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    appUser,
    loading,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
